/**
 * Live Scoring Page - Dedicated page for match scoring
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import { ScorerConsole } from '../components/live-scoring';
import QuickResultModal from '../components/fixtures/QuickResultModal';

export default function LiveScoringPage() {
  const { eventId, fixtureId } = useParams();
  const navigate = useNavigate();
  
  const [fixture, setFixture] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEndMatch, setShowEndMatch] = useState(false);
  
  useEffect(() => {
    fetchData({ initial: true });
  }, [eventId, fixtureId]);
  
  const fetchData = async ({ initial = false } = {}) => {
    try {
      // Only show the full-page loading spinner on initial mount. Subsequent
      // refetches (triggered after every Record Ball / Undo / etc.) must NOT
      // unmount the page, otherwise the scorer's scroll position resets to
      // the top after every delivery.
      if (initial) setLoading(true);
      const [fixtureRes, eventRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${eventId}/fixtures/${fixtureId}`),
        axiosInstance.get(`/events/${eventId}`)
      ]);
      setFixture(fixtureRes.data);
      setEvent(eventRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      if (initial) setError(err.response?.data?.detail || 'Failed to load match data');
      toast.error('Failed to load match data');
    } finally {
      if (initial) setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E54562] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-500">Loading match data...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-md">
          <i className="ph-bold ph-warning-circle text-5xl text-red-500 mb-4"></i>
          <h2 className="text-xl font-bold mb-2">Error Loading Match</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-[#2B3A67] text-white px-6 py-2 rounded-xl font-semibold hover:bg-[#3d4f87]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  if (!fixture) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-md">
          <i className="ph-bold ph-question text-5xl text-gray-400 mb-4"></i>
          <h2 className="text-xl font-bold mb-2">Match Not Found</h2>
          <p className="text-gray-500 mb-6">The requested match could not be found.</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-[#2B3A67] text-white px-6 py-2 rounded-xl font-semibold hover:bg-[#3d4f87]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusStyles = {
    live:      { bg: 'bg-red-500',    text: 'text-white',        pulse: 'animate-pulse', label: '● LIVE' },
    completed: { bg: 'bg-emerald-500', text: 'text-white',        pulse: '',             label: '✓ COMPLETED' },
    scheduled: { bg: 'bg-amber-400',  text: 'text-amber-900',    pulse: '',             label: 'SCHEDULED' },
  };
  const s = statusStyles[fixture.match_status] || statusStyles.scheduled;

  const teamA = fixture.team_a_name || 'TBD';
  const teamB = fixture.team_b_name || 'TBD';
  const logoA = fixture.team_a_logo;
  const logoB = fixture.team_b_logo;

  const fmtScheduled = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
           + ' · ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch { return null; }
  };
  const scheduledLine = fmtScheduled(fixture.scheduled_at);

  const handleEndMatchSubmit = async (payload) => {
    try {
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/set-final-result`,
        payload,
      );
      const summary = res.data?.result_summary;
      toast.success(summary ? `Match ended · ${summary}` : 'Match ended');
      setShowEndMatch(false);
      // Match is closed — bounce back to the fixtures list so the scorer
      // can pick up the next game.
      navigate(`/tournaments/${eventId}?phase=fixtures`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end match');
    }
  };

  const isLive = fixture.match_status === 'live';

  const TeamMini = ({ name, logo, score, wickets, overs }) => (
    <div className="flex items-center gap-1.5 min-w-0">
      {logo ? (
        <img src={logo} alt="" className="w-5 h-5 rounded-full object-cover ring-1 ring-white/20 flex-shrink-0" />
      ) : (
        <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] text-white/80 font-bold flex-shrink-0">
          {name.slice(0, 2).toUpperCase()}
        </div>
      )}
      <span className="text-[11px] font-semibold text-white truncate max-w-[80px]">{name}</span>
      {score != null && (
        <span className="text-[11px] text-white/80 tabular-nums whitespace-nowrap font-medium">
          {score}/{wickets ?? 0}
          {overs != null && <span className="text-white/50"> ({overs})</span>}
        </span>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100" style={{ paddingBottom: 'calc(8rem + env(safe-area-inset-bottom))' }}>
      {/* Compact sticky scorer header — keeps everything essential in ~56px */}
      <header className="bg-[#2B3A67] text-white shadow-md sticky top-0 z-40 pt-safe">
        <div className="max-w-7xl mx-auto px-3 py-2 flex items-center gap-2">
          <Link
            to={`/tournaments/${eventId}?phase=fixtures`}
            className="p-1 -ml-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
            data-testid="scorer-back-btn"
          >
            <i className="ph-bold ph-arrow-left text-base"></i>
          </Link>
          <span className="text-[10px] font-semibold text-white/60 flex-shrink-0">M#{fixture.match_number}</span>
          <span
            className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide ${s.bg} ${s.text} ${s.pulse} flex-shrink-0`}
            data-testid="match-status-badge"
          >{s.label}</span>
          <div className="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
            <TeamMini name={teamA} logo={logoA} score={fixture.team_a_score} wickets={fixture.team_a_wickets} overs={fixture.team_a_overs} />
            <span className="text-white/40 text-[10px] flex-shrink-0">vs</span>
            <TeamMini name={teamB} logo={logoB} score={fixture.team_b_score} wickets={fixture.team_b_wickets} overs={fixture.team_b_overs} />
          </div>
        </div>
      </header>
      
      {/* Scorer Console */}
      <div className="max-w-7xl mx-auto py-3 px-3 sm:py-6 sm:px-4">
        <ScorerConsole 
          eventId={eventId}
          fixtureId={fixtureId}
          fixture={fixture}
          onMatchUpdate={fetchData}
        />
      </div>

      {/* End Match — floating action, live matches only.
          Lets scorers close out rain/walkover scenarios without navigating
          back to the admin fixtures list. */}
      {isLive && (
        <button
          onClick={() => setShowEndMatch(true)}
          data-testid="end-match-btn"
          className="fixed bottom-6 right-4 z-30 bg-[#E54562] hover:bg-[#d13a55] text-white font-bold rounded-full shadow-lg flex items-center gap-2 px-5 py-3 transition-colors"
          style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
          aria-label="End match"
        >
          <i className="ph-bold ph-flag-checkered text-lg"></i>
          <span className="text-sm">End Match</span>
        </button>
      )}

      {showEndMatch && (
        <QuickResultModal
          fixture={fixture}
          onSubmit={handleEndMatchSubmit}
          onClose={() => setShowEndMatch(false)}
        />
      )}
    </div>
  );
}
